/* Shared cart transport. The selector enables it only after subscription launch validation. */
(() => {
  const positiveId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  function buildSelection(products, selections, device) {
    const items = [], discounts = [];
    if (device) {
      if (!device.variant?.available || !positiveId(device.variant.id) || !Number.isSafeInteger(device.quantity) || device.quantity < 1) {
        throw new Error('Choose an available litter box and a valid quantity.');
      }
      items.push({ id: Number(device.variant.id), quantity: device.quantity });
    }
    for (const p of products) {
      const choice = selections[p.key];
      if (!choice?.selected) continue;
      if (!p.available || !positiveId(p.variantId)) throw new Error(`${p.title} is unavailable. Please update your selection.`);
      const item = { id: Number(p.variantId), quantity: 1 };
      if (choice.mode === 'subscription') {
        const plan = p.sellingPlans?.[choice.months];
        if (!plan || !positiveId(plan.id) || plan.billingInterval !== 'MONTH' || plan.deliveryInterval !== 'MONTH' ||
            plan.billingIntervalCount !== choice.months || plan.deliveryIntervalCount !== choice.months || plan.renewalPrice !== p.renewal) {
          throw new Error(`The ${p.title} subscription is not ready. Please refresh or choose a one-time purchase.`);
        }
        if (p.first < p.renewal && !plan.firstOrderDiscountCode) throw new Error(`The ${p.title} first-order offer is not ready.`);
        item.selling_plan = Number(plan.id);
        if (plan.firstOrderDiscountCode) discounts.push(plan.firstOrderDiscountCode);
      } else if (choice.mode !== 'once') {
        throw new Error('Please choose a valid purchase option.');
      }
      items.push(item);
    }
    if (!items.length) throw new Error('Select at least one item.');
    return { items, discounts: [...new Set(discounts)] };
  }
  let busy = false;
  async function add(selection, { root = window.Shopify?.routes?.root || '/', fetcher = window.fetch.bind(window) } = {}) {
    if (busy) throw new Error('Your items are already being added.');
    busy = true;
    let added = false;
    try {
      // Read existing codes before writing so an offer never silently clears a shopper's code.
      let discounts = [];
      if (selection.discounts.length) {
        const response = await fetcher(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Could not check your cart. Please try again.');
        const cart = await response.json();
        discounts = [...new Set([...(cart.discount_codes || []).map(d => d.code), ...selection.discounts])];
      }
      const response = await fetcher(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: selection.items })
      });
      const body = await response.json();
      if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : 'Could not add your selection. Check your cart before trying again.');
      added = true;
      if (discounts.length) {
        const result = await fetcher(`${root}cart/update.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: discounts.join(',') })
        });
        if (!result.ok) throw new Error('Your items were added, but the offer could not be applied. Review your cart before continuing.');
        const updatedCart = await result.json();
        const rejectedOffer = selection.discounts.some(code =>
          !(updatedCart.discount_codes || []).some(discount => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable)
        );
        if (rejectedOffer) throw new Error('Your items were added, but a first-order offer is not eligible. Review the current prices in your cart.');
      }
      return { added: true, cartUrl: `${root}cart`, checkoutUrl: `${root}checkout` };
    } catch (error) {
      error.itemsAdded = added;
      // Never retry a cart write automatically: the server might already have applied it.
      throw error;
    } finally {
      busy = false;
    }
  }
  window.hhRefillCart = { buildSelection, add };
})();
